import React, { useState } from 'react';
import { Table as TableAntd } from 'antd';
import cn from 'classnames';
import { Card } from '_atoms';
import { TableFooter, TableHeader, PopupConfigureTable } from '_molecules';
import { ReactComponent as CircularProgress } from 'dist/icons/loading.svg';
import { ReactComponent as ErrorIcon } from 'dist/icons/error.svg';
import type { TableProps, TableMobileProps, TableRow, TableColumnType } from './types';
import './style.scss';

export function TableMobile<T extends { uuid: string } = TableRow>({
  className,
  data,
  columns,
  empty = null,
  loading,
}: TableMobileProps<T>) {
  return data.length > 0 ? (
    <div className={cn('table-mobile-default__content', className)}>
      {data.map(d => {
        const row = d as T & Record<string, unknown>;
        return (
          <div key={d.uuid} className="item">
            {columns.map(c => {
              const key = c.rowKey ?? c.key ?? '';
              return (
                <div key={key} className="item__row">
                  {!c.hideMobileTitle && (
                    <div className="title">{typeof c.title === 'function' ? c.title(true) : c.title}</div>
                  )}
                  <div className={cn('data', c.className, { extra: c.hideMobileTitle })}>
                    {c.render?.(row[key], d) ?? (row[key] as React.ReactNode)}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
      {loading && (
        <div className="loading-overflow">
          <CircularProgress />
        </div>
      )}
    </div>
  ) : (
    <div className={cn('table-mobile-default__content', className)}>
      {empty}
      {loading && (
        <div className="loading-overflow">
          <CircularProgress />
        </div>
      )}
    </div>
  );
}

export const Table = <T extends object = TableRow>({
  columns,
  data,
  className,
  loading,
  showCardHeader = true,
  showCardFooter = true,
  emptyComponent = null,
  stickyHeader,
  customizable,
  tableName,
  shownColumns = {},
  setShownColumns,
  error,
  cardTitle,
  cardTitleExtra,
  subtitle,
  tooltipInfo,
  limit,
  page,
  cardCount,
  itemsCount,
  updateParams,
  timeSorting,
  onTimeSorting,
  extra,
  innerRef,
  defaultTableMobile,
  ...props
}: TableProps<T>) => {
  const [configurePopupOpen, setConfigurePopupOpen] = useState(false);

  const filteredColumns = React.useMemo(
    () => (Object.keys(shownColumns).length ? columns.filter(c => c.key && shownColumns[c.key]) : columns),
    [columns, shownColumns]
  );

  const updatePage = (value: number) => updateParams?.('page', value);
  const updateLimit = (value: number) => updateParams?.('limit', value);

  return (
    <Card className={cn('table-default-antd', className)}>
      {!!showCardHeader && (
        <TableHeader
          title={cardTitle}
          titleExtra={cardTitleExtra}
          subtitle={subtitle}
          count={cardCount}
          tooltipInfo={tooltipInfo}
          loading={loading}
          sorting={timeSorting}
          onSorting={onTimeSorting}
          extra={extra}
          newLoading
          sticky={stickyHeader}
          customizable={customizable}
          openConfigurePopupOpen={() => setConfigurePopupOpen(true)}
        />
      )}
      {error && (
        <div className="table-default__error">
          <ErrorIcon />
          {error}
        </div>
      )}
      <TableAntd
        ref={innerRef as React.Ref<HTMLDivElement>}
        className="table-default-antd__content"
        dataSource={data}
        columns={filteredColumns}
        pagination={false}
        showSorterTooltip={false}
        sortDirections={['ascend', 'descend', 'ascend']}
        loading={
          loading
            ? {
                indicator: <CircularProgress />,
                spinning: true,
              }
            : false
        }
        tableLayout="fixed"
        locale={{ emptyText: emptyComponent }}
        {...props}
      />
      {defaultTableMobile && (
        <TableMobile
          data={data as TableRow[]}
          columns={filteredColumns as TableColumnType[]}
          empty={emptyComponent}
          loading={loading}
        />
      )}
      {!!showCardFooter && (
        <TableFooter
          limit={limit}
          page={page}
          onChangePage={updatePage}
          onChangeLimit={updateLimit}
          itemsCount={itemsCount}
          loading={loading}
          newLoading
        />
      )}
      <PopupConfigureTable
        columns={columns as TableColumnType[]}
        tableName={tableName}
        shownColumns={shownColumns}
        open={configurePopupOpen}
        onSave={setShownColumns}
        onClose={() => setConfigurePopupOpen(false)}
      />
    </Card>
  );
};
